/** Rail groups (ADMIN_UI_SPEC "Shell") and the breadcrumb model derived from them. */
import type { Capability } from '@agency/shared';
import {
  Activity,
  Archive,
  BookOpen,
  Building,
  Cpu,
  Download,
  Flag,
  Gauge,
  HeartPulse,
  Inbox,
  KeyRound,
  Laptop,
  LayoutDashboard,
  Link,
  Mail,
  Package,
  Plug,
  Radio,
  Rocket,
  ScrollText,
  ShieldCheck,
  Timer,
  UserPlus,
  Users,
  type LucideIcon,
} from 'lucide-react';

export interface NavItemDef {
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  /** Any-of capability needed to see the page's data. */
  capability?: Capability | readonly Capability[];
}

export interface NavGroupDef {
  id: string;
  label: string;
  items: NavItemDef[];
}

const OPS_READ: readonly Capability[] = ['device.manage', 'audit.read'];
const TEMPLATE_READ: readonly Capability[] = ['template.edit', 'template.publish'];

export const NAV_GROUPS: NavGroupDef[] = [
  { id: 'overview', label: 'Overview', items: [{ id: 'overview', label: 'Overview', path: '/', icon: LayoutDashboard }] },
  {
    id: 'people',
    label: 'People and access',
    items: [
      { id: 'people', label: 'People', path: '/people', icon: Users, capability: ['people.manage', 'audit.read', 'account.assign'] },
      { id: 'invitations', label: 'Invitations', path: '/invitations', icon: UserPlus, capability: 'people.manage' },
      { id: 'sessions', label: 'Sessions', path: '/sessions', icon: Radio, capability: 'people.manage' },
    ],
  },
  {
    id: 'accounts',
    label: 'Accounts and devices',
    items: [
      { id: 'accounts', label: 'Accounts', path: '/accounts', icon: Building, capability: ['account.assign', 'account.register', 'audit.read'] },
      { id: 'devices', label: 'Devices', path: '/devices', icon: Laptop, capability: 'device.manage' },
      { id: 'connections', label: 'Connections', path: '/connections', icon: Link, capability: ['account.assign', 'account.register', 'audit.read'] },
    ],
  },
  { id: 'keys', label: 'API keys', items: [{ id: 'api-keys', label: 'OpenAI keys', path: '/api-keys', icon: KeyRound, capability: 'ai_key.manage' }] },
  {
    id: 'templates',
    label: 'Templates and rules',
    items: [
      { id: 'content', label: 'Content', path: '/content', icon: BookOpen, capability: TEMPLATE_READ },
      { id: 'templates', label: 'Templates', path: '/templates', icon: ScrollText, capability: TEMPLATE_READ },
      { id: 'releases', label: 'Releases', path: '/releases', icon: Package, capability: TEMPLATE_READ },
    ],
  },
  { id: 'usage', label: 'Usage', items: [{ id: 'ai-usage', label: 'AI usage', path: '/usage/ai', icon: Activity, capability: ['audit.read', 'ai_key.manage'] }, { id: 'usage', label: 'All usage', path: '/usage', icon: Activity, capability: ['audit.read', 'ai_key.manage'] }] },
  {
    id: 'audit',
    label: 'Audit',
    items: [
      { id: 'audit', label: 'Audit explorer', path: '/audit', icon: ShieldCheck, capability: 'audit.read' },
      { id: 'exports', label: 'Exports', path: '/exports', icon: Download, capability: ['audit.read', 'audit.export'] },
    ],
  },
  {
    id: 'ops',
    label: 'System operations',
    items: [
      { id: 'outbox', label: 'Outbox', path: '/ops/outbox', icon: Inbox, capability: OPS_READ },
      { id: 'mail', label: 'Mail', path: '/ops/mail', icon: Mail, capability: OPS_READ },
      { id: 'release-policy', label: 'Release policy', path: '/ops/release-policy', icon: Rocket, capability: OPS_READ },
      { id: 'rate-limits', label: 'Rate limits', path: '/ops/rate-limits', icon: Gauge, capability: [...OPS_READ, ...TEMPLATE_READ] },
      { id: 'feature-flags', label: 'Feature flags', path: '/ops/feature-flags', icon: Flag, capability: [...OPS_READ, ...TEMPLATE_READ] },
      { id: 'model-config', label: 'Model config', path: '/ops/model-config', icon: Cpu, capability: [...OPS_READ, ...TEMPLATE_READ, 'ai_key.manage'] },
      { id: 'integrations', label: 'Integrations', path: '/ops/integrations', icon: Plug, capability: 'device.manage' },
      { id: 'leases', label: 'Leases', path: '/ops/leases', icon: Timer, capability: OPS_READ },
      { id: 'health', label: 'Health', path: '/ops/health', icon: HeartPulse, capability: OPS_READ },
    ],
  },
];

export const ARCHIVE_ICON = Archive;

export interface NavMatch {
  group: NavGroupDef;
  item: NavItemDef;
}

/** Longest-prefix match of the current pathname against the nav items. */
export function matchNav(pathname: string): NavMatch | null {
  let best: NavMatch | null = null;
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      const hit = item.path === '/' ? pathname === '/' : pathname === item.path || pathname.startsWith(`${item.path}/`);
      if (hit && (!best || item.path.length > best.item.path.length)) best = { group, item };
    }
  }
  return best;
}
